import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Button, Card, ButtonGroup, Form, Nav, Modal, Table } from 'react-bootstrap';
import { Stage, Layer, Rect, Transformer, Group, Text, Line, Arc } from 'react-konva'; // 3D imports removed
import { FaVectorSquare, FaTrash, FaUndo, FaRedo, FaUndoAlt, FaRedoAlt, FaSearchPlus, FaSearchMinus, FaExpand, FaAlignCenter, FaCog, FaLayerGroup } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';
import { FaDrawPolygon, FaFolderOpen, FaSave, FaDownload, FaCogs, FaFileImage, FaFilePdf, FaCube } from 'react-icons/fa';
import useAuthStore from '../stores/authStore';
import axiosInstance from '../api/axiosInstance';
import jsPDF from 'jspdf';
import WaGgu3DViewer from '../components/WaGgu3DViewer';

// --- 기본 가구/도면 요소 프리셋 ---
const ITEM_TYPES = {
    SHELL: 'shell',     // 전체 외곽
    ROOM: 'room',       // 방/구역
    FURNITURE: 'furniture'
};

const ROOM_PRESETS = [
    { type: 'living', name: '거실', color: '#ecf0f1' },
    { type: 'room', name: '방', color: '#dfe6e9' },
    { type: 'kitchen', name: '주방', color: '#ffeaa7' },
    { type: 'bathroom', name: '욕실', color: '#81ecec' },
    { type: 'veranda', name: '베란다', color: '#fab1a0' },
    { type: 'terrace', name: '테라스', color: '#55efc4' },
    { type: 'entrance', name: '현관', color: '#b2bec3' }
];

const FURNITURE_PRESETS = [
    { type: 'sofa', name: '소파', width: 120, height: 60, color: '#ff7675' },
    { type: 'bed', name: '침대', width: 150, height: 200, color: '#74b9ff' },
    { type: 'table', name: '테이블', width: 80, height: 80, color: '#55efc4' },
    { type: 'closet', name: '옷장', width: 180, height: 60, color: '#fab1a0' },
    { type: 'chair', name: '의자', width: 40, height: 40, color: '#a29bfe' },
    { type: 'door', name: '문', width: 100, height: 100, color: '#e17055' },
    { type: 'sliding-door', name: '미닫이문', width: 120, height: 10, color: '#e17055' },
    { type: 'window', name: '창문', width: 120, height: 20, color: '#81ecec' },
    { type: 'plant', name: '화분', width: 40, height: 40, color: '#55efc4' }
];

const AREA_PRESETS = [33, 59, 84, 86, 114, 132, 165, 198]; // 86m2 = 26py

const SAMPLES = [
    {
        id: 1, title: '사회초년생 원룸 스튜디오', type: 'Studio', desc: '주방과 거실이 통합된 효율적인 1인 가구 공간', items: [
            { id: 's1', type: ITEM_TYPES.SHELL, name: '외곽', x: 0, y: 0, width: 450, height: 600, rotation: 0, color: 'transparent', stroke: '#2d3436', strokeWidth: 5, draggable: true },
            { id: 'r1', type: ITEM_TYPES.ROOM, subType: 'living', name: '거실/침실', x: 20, y: 150, width: 410, height: 430, rotation: 0, color: '#ecf0f1', draggable: true, opacity: 0.5 },
            { id: 'r2', type: ITEM_TYPES.ROOM, subType: 'bathroom', name: '욕실', x: 20, y: 20, width: 150, height: 120, rotation: 0, color: '#81ecec', draggable: true, opacity: 0.5 },
            { id: 'r3', type: ITEM_TYPES.ROOM, subType: 'kitchen', name: '주방', x: 180, y: 20, width: 250, height: 120, rotation: 0, color: '#ffeaa7', draggable: true, opacity: 0.5 },
            { id: 'f1', type: ITEM_TYPES.FURNITURE, subType: 'bed', name: '침대', x: 50, y: 350, width: 150, height: 200, rotation: 0, color: '#74b9ff', draggable: true },
            { id: 'f2', type: ITEM_TYPES.FURNITURE, subType: 'closet', name: '옷장', x: 250, y: 350, width: 150, height: 60, rotation: 180, color: '#fab1a0', draggable: true },
            { id: 'f3', type: ITEM_TYPES.FURNITURE, subType: 'table', name: '책상', x: 250, y: 450, width: 120, height: 60, rotation: 0, color: '#55efc4', draggable: true },
            { id: 'd1', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '현관문', x: 380, y: 580, width: 60, height: 10, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd2', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '욕실문', x: 130, y: 140, width: 40, height: 10, rotation: 0, color: '#e17055', draggable: true },
            { id: 'w1', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '큰창문', x: 100, y: 590, width: 150, height: 10, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w2', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '주방창', x: 300, y: 10, width: 80, height: 10, rotation: 0, color: '#81ecec', draggable: true }
        ]
    },
    {
        id: 2, title: '1.5룸 신축 빌라', type: '1.5 Room', desc: '거실과 침실이 완벽히 분리된 쾌적한 구조', items: [
            { id: 's2', type: ITEM_TYPES.SHELL, name: '외곽', x: 0, y: 0, width: 700, height: 500, rotation: 0, color: 'transparent', stroke: '#2d3436', strokeWidth: 5, draggable: true },
            { id: 'r2-1', type: ITEM_TYPES.ROOM, subType: 'living', name: '거실', x: 20, y: 20, width: 400, height: 460, rotation: 0, color: '#ecf0f1', draggable: true, opacity: 0.5 },
            { id: 'r2-2', type: ITEM_TYPES.ROOM, subType: 'room', name: '침실', x: 430, y: 20, width: 250, height: 300, rotation: 0, color: '#dfe6e9', draggable: true, opacity: 0.5 },
            { id: 'r2-3', type: ITEM_TYPES.ROOM, subType: 'bathroom', name: '욕실', x: 430, y: 330, width: 250, height: 150, rotation: 0, color: '#81ecec', draggable: true, opacity: 0.5 },
            { id: 'f2-1', type: ITEM_TYPES.FURNITURE, subType: 'sofa', name: '소파', x: 50, y: 100, width: 180, height: 80, rotation: 0, color: '#ff7675', draggable: true },
            { id: 'f2-2', type: ITEM_TYPES.FURNITURE, subType: 'table', name: '식탁', x: 280, y: 350, width: 100, height: 80, rotation: 0, color: '#55efc4', draggable: true },
            { id: 'd2-1', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '현관', x: 0, y: 200, width: 10, height: 100, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd2-2', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '방문', x: 420, y: 100, width: 10, height: 60, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd2-3', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '욕실문', x: 420, y: 380, width: 10, height: 50, rotation: 0, color: '#e17055', draggable: true },
            { id: 'w2-1', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '거실창', x: 100, y: 10, width: 150, height: 10, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w2-2', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '침실창', x: 480, y: 10, width: 120, height: 10, rotation: 0, color: '#81ecec', draggable: true }
        ]
    },
    {
        id: 3, title: '24평형 화이트 인테리어', type: '2 Room', desc: '59㎡ 표준 2베이 판상형 아파트 템플릿', items: [
            { id: 's3', type: ITEM_TYPES.SHELL, name: '외곽', x: 0, y: 0, width: 850, height: 750, rotation: 0, color: 'transparent', stroke: '#2d3436', strokeWidth: 5, draggable: true },
            { id: 'r3-1', type: ITEM_TYPES.ROOM, subType: 'living', name: '거실', x: 300, y: 300, width: 520, height: 420, rotation: 0, color: '#ecf0f1', draggable: true, opacity: 0.3 },
            { id: 'r3-2', type: ITEM_TYPES.ROOM, subType: 'kitchen', name: '주방', x: 300, y: 30, width: 520, height: 250, rotation: 0, color: '#ffeaa7', draggable: true, opacity: 0.3 },
            { id: 'r3-3', type: ITEM_TYPES.ROOM, subType: 'room', name: '안방', x: 30, y: 400, width: 250, height: 320, rotation: 0, color: '#dfe6e9', draggable: true, opacity: 0.3 },
            { id: 'r3-4', type: ITEM_TYPES.ROOM, subType: 'room', name: '작은방', x: 30, y: 30, width: 250, height: 250, rotation: 0, color: '#dfe6e9', draggable: true, opacity: 0.3 },
            { id: 'f3-1', type: ITEM_TYPES.FURNITURE, subType: 'sofa', name: '거실 소파', x: 350, y: 350, width: 240, height: 90, rotation: 0, color: '#ff7675', draggable: true },
            { id: 'f3-2', type: ITEM_TYPES.FURNITURE, subType: 'bed', name: '퀸베드', x: 60, y: 450, width: 150, height: 200, rotation: 0, color: '#74b9ff', draggable: true },
            { id: 'd3-1', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '현관문', x: 840, y: 100, width: 10, height: 80, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd3-2', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '안방문', x: 280, y: 450, width: 10, height: 60, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd3-3', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '작은방문', x: 280, y: 150, width: 10, height: 60, rotation: 0, color: '#e17055', draggable: true },
            { id: 'w3-1', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '거실발코니창', x: 450, y: 710, width: 250, height: 10, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w3-2', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '주방창', x: 500, y: 10, width: 100, height: 10, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w3-3', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '안방창', x: 80, y: 710, width: 150, height: 10, rotation: 0, color: '#81ecec', draggable: true }
        ]
    },
    {
        id: 4, title: '34평형 최신 4베이', type: '3 Room', desc: '84㎡ 맞통풍 구조의 신축 아파트 평면도', items: [
            { id: 's4', type: ITEM_TYPES.SHELL, name: '외곽', x: 0, y: 0, width: 1300, height: 850, rotation: 0, color: 'transparent', stroke: '#2d3436', strokeWidth: 5, draggable: true },
            { id: 'r4-1', type: ITEM_TYPES.ROOM, subType: 'living', name: '거실', x: 450, y: 350, width: 400, height: 450, rotation: 0, color: '#ecf0f1', draggable: true, opacity: 0.3 },
            { id: 'r4-2', type: ITEM_TYPES.ROOM, subType: 'kitchen', name: '주방', x: 450, y: 50, width: 400, height: 280, rotation: 0, color: '#ffeaa7', draggable: true, opacity: 0.3 },
            { id: 'r4-3', type: ITEM_TYPES.ROOM, subType: 'room', name: '안방', x: 870, y: 350, width: 400, height: 450, rotation: 0, color: '#dfe6e9', draggable: true, opacity: 0.3 },
            { id: 'r4-4', type: ITEM_TYPES.ROOM, subType: 'room', name: '자녀방1', x: 50, y: 350, width: 180, height: 450, rotation: 0, color: '#dfe6e9', draggable: true, opacity: 0.3 },
            { id: 'r4-5', type: ITEM_TYPES.ROOM, subType: 'room', name: '자녀방2', x: 250, y: 350, width: 180, height: 450, rotation: 0, color: '#dfe6e9', draggable: true, opacity: 0.3 },
            { id: 'd4-1', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '현관', x: 50, y: 150, width: 10, height: 100, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd4-2', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '안방문', x: 860, y: 450, width: 10, height: 70, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd4-3', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '방1문', x: 230, y: 450, width: 10, height: 60, rotation: 0, color: '#e17055', draggable: true },
            { id: 'd4-4', type: ITEM_TYPES.FURNITURE, subType: 'door', name: '방2문', x: 430, y: 450, width: 10, height: 60, rotation: 0, color: '#e17055', draggable: true },
            { id: 'w4-1', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '거실창', x: 450, y: 790, width: 400, height: 15, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w4-2', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '주방창', x: 600, y: 40, width: 100, height: 10, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w4-3', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '안방창', x: 970, y: 790, width: 220, height: 10, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w4-4', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '방1창', x: 80, y: 790, width: 120, height: 10, rotation: 0, color: '#81ecec', draggable: true },
            { id: 'w4-5', type: ITEM_TYPES.FURNITURE, subType: 'window', name: '방2창', x: 280, y: 790, width: 120, height: 10, rotation: 0, color: '#81ecec', draggable: true }
        ]
    }
];

const WaGgu = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [items, setItems] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [projectName] = useState('우리집 와꾸'); // keeping as fallback
    const [currentMapTitle, setCurrentMapTitle] = useState(null);
    const [currentMapId, setCurrentMapId] = useState(null);

    const itemsRef = useRef(items);
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    // 모바일 대응 state
    const isMobile = useMediaQuery({ query: '(max-width: 991px)' });
    const [mobileTab, setMobileTab] = useState('canvas'); // 'menu', 'canvas', 'settings'

    // CRUD State
    const { isAuthenticated } = useAuthStore();
    const [showLoadModal, setShowLoadModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [tempSaveTitle, setTempSaveTitle] = useState('');
    const [savedItems, setSavedItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const itemsPerPage = 10;

    // 창 크기 변화 및 탭 전환 시 Stage 리사이즈 유도
    useEffect(() => {
        const timer = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            if (stageRef.current) {
                stageRef.current.batchDraw();
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [mobileTab, isMobile, items.length]);
    const [showDimensions, setShowDimensions] = useState(true);
    const [guides, setGuides] = useState([]); // 스마트 가이드 라인

    // 점자 그리드 패턴 최적화 (useMemo)
    const gridPattern = useMemo(() => {
        const canvas = document.createElement('canvas');
        canvas.width = 20;
        canvas.height = 20;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#a0aec0'; // 좀 더 진한 회색 (모눈 느낌)
        ctx.beginPath();
        // 점을 캔버스 중앙(10, 10)에 배치하여 패턴 연결 시 정렬되도록 함
        ctx.arc(10, 10, 1.5, 0, Math.PI * 2);
        ctx.fill();
        return canvas;
    }, []);

    // Zoom & Pan
    const [stageScale, setStageScale] = useState(1);
    const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
    const stageScaleRef = useRef(stageScale);
    const stagePosRef = useRef(stagePos);
    useEffect(() => {
        stageScaleRef.current = stageScale;
        stagePosRef.current = stagePos;
    }, [stageScale, stagePos]);

    // Modal states
    const [showShellModal, setShowShellModal] = useState(false);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [shellConfig, setShellConfig] = useState({ width: 1070, height: 800 });
    const shellConfigRef = useRef(shellConfig);
    useEffect(() => {
        shellConfigRef.current = shellConfig;
    }, [shellConfig]);
    const [areaUnit, setAreaUnit] = useState('m2'); // 'm2' or 'py'
    const [shellRatio, setShellRatio] = useState('4:3'); // '4:3' or '1:1'
    const [selectedPresetArea, setSelectedPresetArea] = useState(null);
    const [pendingSample, setPendingSample] = useState(null); // 샘플 선택 지연 로딩용
    const [is3DMode, setIs3DMode] = useState(false); // 3D 토글 모드

    const [history, setHistory] = useState([]);
    const [historyStep, setHistoryStep] = useState(-1);

    const stageRef = useRef();
    const trRef = useRef();
    const containerRef = useRef();

    // --- CRUD 로직 시작 ---
    const fetchSavedItems = useCallback(async (page = 1) => {
        if (!isAuthenticated) return;
        setIsLoadingList(true);
        try {
            const offset = (page - 1) * itemsPerPage;
            // board_id=4 for WaGgu
            const response = await axiosInstance.get(`posts?board_id=4&OFFSET=${offset}`);
            if (response.data.status === '0000') {
                const rawList = response.data.data.postList || [];
                const formattedList = rawList.map(item => item.json || item);
                setSavedItems(formattedList);
                setTotalCount(response.data.data.postCount || 1);
            } else {
                setSavedItems([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Failed to fetch saved items:', error);
        } finally {
            setIsLoadingList(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (showLoadModal) fetchSavedItems(currentPage);
    }, [showLoadModal, currentPage, fetchSavedItems]);

    const handleSave = async (e) => {
        if (e) e.preventDefault();

        const targetTitle = tempSaveTitle.trim() || currentMapTitle || projectName;
        if (!targetTitle) {
            alert('제목을 입력해주세요.');
            return;
        }

        setIsSaving(true);
        try {
            // 가장 최신의 상태 보장을 위해 Ref 사용
            const currentData = {
                items: itemsRef.current,
                shellConfig: shellConfigRef.current,
                projectName, // Legacy
                currentMapTitle: targetTitle,
                areaUnit,
                shellRatio,
                stageScale: stageScaleRef.current,
                stagePos: stagePosRef.current
            };

            const payload = {
                slug: 'waggu',
                title: targetTitle,
                extra_data: currentData,
                is_notice: true // n8n 처리용
            };

            let response;
            if (currentMapId) {
                response = await axiosInstance.put('posts', { ...payload, id: currentMapId });
            } else {
                // n8n에 전달 시 추가 파라미터가 필요한 경우 포함 (DevToolbox에서는 posts 엔드포인트에 board_id 지정이 webhook 내에서 처리됨)
                // 명시적으로 board_id를 넘기거나 n8n 워크플로우를 가정합니다 (slug 기반 분기 등).
                payload.board_id = 4;
                response = await axiosInstance.post('posts', payload);
            }

            if (response.data.status === '0000') {
                alert('저장되었습니다.');
                setShowSaveModal(false);
                setTempSaveTitle('');
                setCurrentMapId(currentMapId || response.data.data?.id);
                setCurrentMapTitle(targetTitle);
                fetchSavedItems(currentPage);
            } else {
                alert(response.data.message || '저장에 실패했습니다.');
            }
        } catch (error) {
            console.error('Save error:', error);
            alert('서버 통신 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleLoadItem = async (item) => {
        if (!item.extra_data) {
            try {
                const response = await axiosInstance.get(`postDetail?post_id=${item.id}`);
                if (response.data.status === '0000') {
                    const detail = response.data.data.postList[0];
                    applyItemData(item.id, item.title, detail.json || detail);
                }
            } catch (error) {
                console.error('Load detail error:', error);
                alert('상세 정보를 불러오지 못했습니다.');
            }
        } else {
            applyItemData(item.id, item.title, item);
        }
    };

    const applyItemData = (id, title, item) => {
        const data = item.extra_data;
        if (!data) {
            alert('데이터 형식이 올바르지 않습니다.');
            return;
        }

        setCurrentMapId(id);
        setCurrentMapTitle(item.title);
        if (data.items) {
            setItems(data.items);
            commitHistory(data.items);
        }
        if (data.shellConfig) setShellConfig(data.shellConfig);
        if (data.areaUnit) setAreaUnit(data.areaUnit);
        if (data.shellRatio) setShellRatio(data.shellRatio);

        if (data.stageScale !== undefined && data.stagePos) {
            setStageScale(data.stageScale);
            setStagePos(data.stagePos);
        } else {
            setTimeout(handleFitToScreen, 100);
        }

        setShowLoadModal(false);
    };

    const handleDeleteServerItem = async (id) => {
        if (!window.confirm('정말로 삭제하시겠습니까?')) return;
        try {
            const response = await axiosInstance.delete(`posts`, { data: { id } });
            if (response.data.status === '0000') {
                alert('삭제되었습니다.');
                if (currentMapId === id) {
                    setCurrentMapId(null);
                }
                fetchSavedItems(currentPage);
            }
        } catch (error) {
            console.error('Delete error:', error);
            alert('삭제에 실패했습니다.');
        }
    };

    const handleExtract = () => {
        try {
            if (items.length === 0) {
                alert('내보낼 도면 데이터가 없습니다.');
                return;
            }
            const dataToExtract = {
                items,
                shellConfig,
                projectName,
                areaUnit,
                shellRatio
            };
            const jsonStr = JSON.stringify(dataToExtract, null, 2);
            const blob = new Blob([jsonStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `waggu_${new Date().getTime()}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Extract error', e);
            alert('추출 중 오류가 발생했습니다.');
        }
    };
    const getBoundingBox = () => {
        if (itemsRef.current.length === 0) return { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100 };
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        itemsRef.current.forEach(item => {
            minX = Math.min(minX, item.x);
            minY = Math.min(minY, item.y);
            maxX = Math.max(maxX, item.x + item.width);
            maxY = Math.max(maxY, item.y + item.height);
        });
        // padding을 넉넉하게 주어 외곽 치수선과 텍스트가 잘리지 않게 방지
        minX -= 100; minY -= 100; maxX += 100; maxY += 100;
        return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
    };

    const handleExportPNG = () => {
        try {
            if (items.length === 0) {
                alert('내보낼 도면 데이터가 없습니다.');
                return;
            }
            const stage = stageRef.current.getStage();
            const box = getBoundingBox();

            const dataURL = stage.toDataURL({
                pixelRatio: 2,
                x: box.minX * stage.scaleX() + stage.x(),
                y: box.minY * stage.scaleY() + stage.y(),
                width: box.width * stage.scaleX(),
                height: box.height * stage.scaleY(),
            });

            const a = document.createElement('a');
            a.href = dataURL;
            a.download = `${currentMapTitle || projectName}_${Date.now()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (e) {
            console.error('PNG extract error', e);
            alert('PNG 변환 중 오류가 발생했습니다.');
        }
    };

    const handleExportPDF = () => {
        try {
            if (items.length === 0) {
                alert('내보낼 도면 데이터가 없습니다.');
                return;
            }
            // A4: 210 x 297 mm
            const stage = stageRef.current.getStage();
            const box = getBoundingBox();

            const dataURL = stage.toDataURL({
                pixelRatio: 2,
                x: box.minX * stage.scaleX() + stage.x(),
                y: box.minY * stage.scaleY() + stage.y(),
                width: box.width * stage.scaleX(),
                height: box.height * stage.scaleY(),
            });

            const isLandscape = box.width > box.height;
            const orientation = isLandscape ? 'landscape' : 'portrait';
            const pdf = new jsPDF(orientation, 'mm', 'a4');

            const pdfW = pdf.internal.pageSize.getWidth();
            const pdfH = pdf.internal.pageSize.getHeight();

            // 비율에 맞게 크기 조절
            const imgRatio = box.width / box.height;
            const pdfRatio = pdfW / pdfH;

            let renderW = pdfW;
            let renderH = pdfH;

            if (imgRatio > pdfRatio) {
                // 이미지가 더 가로로 길 때
                renderW = pdfW;
                renderH = pdfW / imgRatio;
            } else {
                // 이미지가 더 세로로 길 때
                renderH = pdfH;
                renderW = pdfH * imgRatio;
            }

            const xPos = (pdfW - renderW) / 2;
            const yPos = (pdfH - renderH) / 2;

            pdf.addImage(dataURL, 'PNG', xPos, yPos, renderW, renderH);
            pdf.save(`${currentMapTitle || projectName}_${Date.now()}.pdf`);
        } catch (e) {
            console.error('PDF extract error', e);
            alert('PDF 변환 중 오류가 발생했습니다.');
        }
    };

    // --- CRUD 로직 끝 ---

    // --- 스마트 가이드 로직 ---
    const getGuides = (movingItem, allItems) => {
        const GUIDELINE_OFFSET = 5;
        const resultGuides = [];

        // 스냅 대상이 될 좌표들 모으기 (자신 제외)
        const stops = { vertical: [], horizontal: [] };
        allItems.forEach(item => {
            if (item.id === movingItem.id()) return;

            // 수직 스냅선 (x 좌표)
            stops.vertical.push(item.x); // 왼쪽
            stops.vertical.push(item.x + item.width / 2); // 중앙
            stops.vertical.push(item.x + item.width); // 오른쪽

            // 수평 스냅선 (y 좌표)
            stops.horizontal.push(item.y); // 상단
            stops.horizontal.push(item.y + item.height / 2); // 중앙
            stops.horizontal.push(item.y + item.height); // 하단
        });

        const itemBounds = {
            left: movingItem.x(),
            middle: movingItem.x() + movingItem.width() / 2,
            right: movingItem.x() + movingItem.width(),
            top: movingItem.y(),
            center: movingItem.y() + movingItem.height() / 2,
            bottom: movingItem.y() + movingItem.height()
        };

        const minDiffV = { diff: GUIDELINE_OFFSET, stop: null, orientation: 'V' };
        const minDiffH = { diff: GUIDELINE_OFFSET, stop: null, orientation: 'H' };

        // 가장 가까운 수직선 찾기
        stops.vertical.forEach(stop => {
            ['left', 'middle', 'right'].forEach(origin => {
                const diff = Math.abs(stop - itemBounds[origin]);
                if (diff < minDiffV.diff) {
                    minDiffV.diff = diff;
                    minDiffV.stop = stop;
                    minDiffV.origin = origin;
                }
            });
        });

        // 가장 가까운 수평선 찾기
        stops.horizontal.forEach(stop => {
            ['top', 'center', 'bottom'].forEach(origin => {
                const diff = Math.abs(stop - itemBounds[origin]);
                if (diff < minDiffH.diff) {
                    minDiffH.diff = diff;
                    minDiffH.stop = stop;
                    minDiffH.origin = origin;
                }
            });
        });

        if (minDiffV.stop !== null) {
            resultGuides.push({ orientation: 'V', pos: minDiffV.stop });
        }
        if (minDiffH.stop !== null) {
            resultGuides.push({ orientation: 'H', pos: minDiffH.stop });
        }

        return { guides: resultGuides, minDiffV, minDiffH };
    };

    const commitHistory = useCallback((newItems) => {
        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push(JSON.stringify(newItems));
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);
    }, [history, historyStep]);

    const handleUndo = () => {
        if (historyStep > 0) {
            const nextStep = historyStep - 1;
            setItems(JSON.parse(history[nextStep]));
            setHistoryStep(nextStep);
        }
    };

    const handleRedo = () => {
        if (historyStep < history.length - 1) {
            const nextStep = historyStep + 1;
            setItems(JSON.parse(history[nextStep]));
            setHistoryStep(nextStep);
        }
    };

    const deleteSelected = useCallback(() => {
        if (!selectedId) return;
        const nextItems = items.filter(item => item.id !== selectedId);
        setItems(nextItems);
        setSelectedId(null);
        commitHistory(nextItems);
    }, [selectedId, items, commitHistory]);

    // Keyboard Delete key event
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
                deleteSelected();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [deleteSelected]);

    // --- 가구 추가 ---
    const addFurniture = (preset) => {
        const id = `f-${Date.now()}`;
        const stage = stageRef.current;
        const centerX = (stage.width() / 2 - stage.x()) / stage.scaleX();
        const centerY = (stage.height() / 2 - stage.y()) / stage.scaleY();

        const newItem = {
            id,
            type: ITEM_TYPES.FURNITURE,
            subType: preset.type,
            name: preset.name,
            customName: preset.name,
            x: centerX - preset.width / 2,
            y: centerY - preset.height / 2,
            width: preset.width,
            height: preset.height,
            rotation: 0,
            color: preset.color,
            draggable: true
        };
        const nextItems = [...items, newItem];
        setItems(nextItems);
        commitHistory(nextItems);
        setSelectedId(id);
    };

    const addRoom = (preset) => {
        const id = `room-${Date.now()}`;
        const stage = stageRef.current;
        const centerX = (stage.width() / 2 - stage.x()) / stage.scaleX();
        const centerY = (stage.height() / 2 - stage.y()) / stage.scaleY();

        const newRoom = {
            id,
            type: ITEM_TYPES.ROOM,
            subType: preset.type,
            name: preset.name,
            x: centerX - 100,
            y: centerY - 75,
            width: 200,
            height: 150,
            rotation: 0,
            color: preset.color,
            draggable: true,
            opacity: 0.5
        };
        const nextItems = [...items, newRoom];
        setItems(nextItems);
        commitHistory(nextItems);
        setSelectedId(id);
    };

    const handleFitToScreen = useCallback(() => {
        if (items.length === 0) {
            setStageScale(1);
            setStagePos({ x: 0, y: 0 });
            return;
        }

        const stage = stageRef.current;
        if (!stage) return;

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        items.forEach(item => {
            minX = Math.min(minX, item.x);
            minY = Math.min(minY, item.y);
            maxX = Math.max(maxX, item.x + item.width);
            maxY = Math.max(maxY, item.y + item.height);
        });

        const padding = 100;
        const contentW = maxX - minX;
        const contentH = maxY - minY;
        const stageW = stage.width();
        const stageH = stage.height();

        if (contentW === 0 || contentH === 0) return;

        const scaleX = (stageW - padding) / contentW;
        const scaleY = (stageH - padding) / contentH;
        const newScale = Math.min(scaleX, scaleY, 1.2);

        setStageScale(newScale);

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        setStagePos({
            x: stageW / 2 - centerX * newScale,
            y: stageH / 2 - centerY * newScale
        });
    }, [items]);

    const createExteriorShell = () => {
        const id = `shell-${Date.now()}`;
        const newShell = {
            id,
            type: ITEM_TYPES.SHELL,
            name: '전체 외곽',
            x: 50,
            y: 50,
            width: shellConfig.width,
            height: shellConfig.height,
            rotation: 0,
            color: 'transparent',
            stroke: '#2d3436',
            strokeWidth: 5,
            draggable: true
        };
        const nextItems = [newShell];
        setItems(nextItems);
        commitHistory(nextItems);
        setShowShellModal(false);
        setCurrentStep(2);
        setSelectedId(id);

        setTimeout(handleFitToScreen, 50);
    };

    const loadSample = (sample) => {
        setPendingSample(sample);
        setShowGalleryModal(false);
    };

    const applySample = (sample) => {
        if (!sample) return;
        setItems(sample.items);
        commitHistory(sample.items);
        if (isMobile) setMobileTab('canvas');

        // 데이터 로드 후 레이아웃이 적용된 상태에서 피팅
        setTimeout(() => {
            handleFitToScreen();
        }, 100);
        setPendingSample(null);
    };

    // --- 확대/축소 로직 ---
    const handleWheel = (e) => {
        e.evt.preventDefault();
        const stage = stageRef.current;
        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        const mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale,
        };

        const newScale = e.evt.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;
        setStageScale(newScale);
        setStagePos({
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
        });
    };

    const handleZoomIn = () => {
        const stage = stageRef.current;
        const oldScale = stage.scaleX();
        const centerX = stage.width() / 2;
        const centerY = stage.height() / 2;
        const mousePointTo = { x: (centerX - stage.x()) / oldScale, y: (centerY - stage.y()) / oldScale };
        const newScale = oldScale * 1.2;
        setStageScale(newScale);
        setStagePos({ x: centerX - mousePointTo.x * newScale, y: centerY - mousePointTo.y * newScale });
    };

    const handleZoomOut = () => {
        const stage = stageRef.current;
        const oldScale = stage.scaleX();
        const centerX = stage.width() / 2;
        const centerY = stage.height() / 2;
        const mousePointTo = { x: (centerX - stage.x()) / oldScale, y: (centerY - stage.y()) / oldScale };
        const newScale = oldScale / 1.2;
        setStageScale(newScale);
        setStagePos({ x: centerX - mousePointTo.x * newScale, y: centerY - mousePointTo.y * newScale });
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    const handleRotatePlan = (direction) => {
        const shellItem = itemsRef.current.find(i => i.type === ITEM_TYPES.SHELL);
        if (!shellItem) return;

        const rad = (shellItem.rotation || 0) * (Math.PI / 180);
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const cx = shellItem.x + (shellItem.width / 2) * cos - (shellItem.height / 2) * sin;
        const cy = shellItem.y + (shellItem.width / 2) * sin + (shellItem.height / 2) * cos;

        const nextItems = itemsRef.current.map(item => {
            const dx = item.x - cx;
            const dy = item.y - cy;
            let nx = cx, ny = cy;
            if (direction === 1) { // 90 CW
                nx = cx - dy;
                ny = cy + dx;
            } else if (direction === -1) { // -90 CCW
                nx = cx + dy;
                ny = cy - dx;
            }
            return {
                ...item,
                x: nx,
                y: ny,
                rotation: ((item.rotation || 0) + direction * 90) % 360
            };
        });

        setItems(nextItems);
        commitHistory(nextItems);

        setTimeout(() => handleFitToScreen(), 100);
    };

    // --- 선택 및 속성 변경 ---
    const handleSelect = (e) => {
        if (e.target === e.target.getStage()) {
            setSelectedId(null);
            return;
        }
        if (e.target.getParent() && e.target.getParent().className === 'Transformer') return;

        let id = e.target.id();
        if (!id) {
            let p = e.target.getParent();
            if (p) {
                id = p.id();
                if (!id && p.getParent()) id = p.getParent().id();
            }
        }
        setSelectedId(id);
    };

    const handleTransformEnd = (e) => {
        const node = e.target;
        const id = node.id();

        let newWidth = node.width() * node.scaleX();
        let newHeight = node.height() * node.scaleY();

        newWidth = Math.max(10, Math.round(newWidth));
        newHeight = Math.max(10, Math.round(newHeight));

        const nextItems = items.map(item => {
            if (item.id === id) {
                return {
                    ...item,
                    x: node.x(),
                    y: node.y(),
                    width: newWidth,
                    height: newHeight,
                    rotation: node.rotation()
                };
            }
            return item;
        });
        setItems(nextItems);
        commitHistory(nextItems);

        node.scaleX(1);
        node.scaleY(1);
    };

    const handleDragMove = (e) => {
        const node = e.target;
        if (node === stageRef.current) return;

        const { guides: newGuides, minDiffV, minDiffH } = getGuides(node, items);
        setGuides(newGuides);

        // 스냅 적용
        if (minDiffV.stop !== null) {
            if (minDiffV.origin === 'left') node.x(minDiffV.stop);
            else if (minDiffV.origin === 'middle') node.x(minDiffV.stop - node.width() / 2);
            else if (minDiffV.origin === 'right') node.x(minDiffV.stop - node.width());
        }
        if (minDiffH.stop !== null) {
            if (minDiffH.origin === 'top') node.y(minDiffH.stop);
            else if (minDiffH.origin === 'center') node.y(minDiffH.stop - node.height() / 2);
            else if (minDiffH.origin === 'bottom') node.y(minDiffH.stop - node.height());
        }
    };

    const handleDragEnd = (e) => {
        setGuides([]); // 드래그 종료 시 가이드 상시 제거
        const node = e.target;
        if (node === stageRef.current) {
            setStagePos({ x: node.x(), y: node.y() });
            return;
        }
        const id = node.id();
        const nextItems = items.map(item => {
            if (item.id === id) {
                return { ...item, x: node.x(), y: node.y() };
            }
            return item;
        });
        setItems(nextItems);
        commitHistory(nextItems);
    };

    const updateItemProperty = (id, key, value) => {
        const nextItems = items.map(i => i.id === id ? { ...i, [key]: value } : i);
        setItems(nextItems);
    };

    // --- 기호 렌더링 ---
    const DoorSymbol = ({ width, height, color }) => (
        <Group>
            {/* 문턱/프레임 영역 - 메뉴 색상과 일관성을 위해 배경에 연하게 색상 적용 */}
            <Rect width={width} height={height} fill={color} opacity={0.1} stroke="#ccc" strokeWidth={1} />
            <Rect width={width} height={height} fill="transparent" stroke="#ccc" strokeWidth={1} />
            {/* 문 Leaf (세로선) */}
            <Line points={[0, 0, 0, width]} stroke={color} strokeWidth={4} />
            {/* 개폐 궤적 */}
            <Arc innerRadius={0} outerRadius={width} angle={90} fill="transparent" stroke={color} strokeWidth={1.5} dash={[4, 2]} rotation={0} x={0} y={0} />
        </Group>
    );

    const WindowSymbol = ({ width, height, color }) => (
        <Group>
            {/* 창 프레임 - 메뉴 색상과 일관성을 위해 배경에 연하게 색상 적용 */}
            <Rect width={width} height={height} fill={color} opacity={0.1} stroke="#ccc" strokeWidth={1} />
            <Rect width={width} height={height} fill="transparent" stroke="#ccc" strokeWidth={1} />
            <Line points={[0, height / 2, width, height / 2]} stroke={color} strokeWidth={4} />
            <Line points={[0, height / 4, width, height / 4]} stroke={color} strokeWidth={1} />
            <Line points={[0, height * 3 / 4, width, height * 3 / 4]} stroke={color} strokeWidth={1} />
        </Group>
    );

    const SlidingDoorSymbol = ({ width, height, color }) => (
        <Group>
            <Rect width={width} height={height} fill={color} opacity={0.1} stroke="#ccc" strokeWidth={1} />
            <Rect width={width} height={height} fill="transparent" stroke="#ccc" strokeWidth={1} />
            <Rect width={width / 2 + 5} height={height / 2} y={0} fill={color} stroke="#333" strokeWidth={1} />
            <Rect x={width / 2 - 5} width={width / 2 + 5} height={height / 2} y={height / 2} fill={color} stroke="#333" strokeWidth={1} />
        </Group>
    );

    // --- 가구/공간 속성 변경 ---

    useEffect(() => {
        if (selectedId && trRef.current && stageRef.current) {
            const selectedNode = stageRef.current.findOne('#' + selectedId);
            if (selectedNode) { trRef.current.nodes([selectedNode]); trRef.current.getLayer().batchDraw(); }
        }
    }, [selectedId, items]);

    const calculateArea = (w, h) => {
        const m2 = (w * h) / 10000;
        const py = m2 / 3.3058;
        return { m2: m2.toFixed(1), py: py.toFixed(1) };
    };

    const handleAreaPreset = (m2) => {
        setSelectedPresetArea(m2);
        let ratioVal = 4 / 3;
        if (shellRatio === '1:1') ratioVal = 1;

        const areaCm2 = m2 * 10000;
        const targetH = Math.sqrt(areaCm2 / ratioVal);
        const targetW = targetH * ratioVal;
        setShellConfig({ width: Math.round(targetW), height: Math.round(targetH) });
    };

    const handleRatioChange = (ratio) => {
        setShellRatio(ratio);
        if (selectedPresetArea) {
            let ratioVal = ratio === '4:3' ? 4 / 3 : 1;
            const areaCm2 = selectedPresetArea * 10000;
            const targetH = Math.sqrt(areaCm2 / ratioVal);
            const targetW = targetH * ratioVal;
            setShellConfig({ width: Math.round(targetW), height: Math.round(targetH) });
        }
    };

    const selectedItem = items.find(i => i.id === selectedId);

    return (
        <Container fluid className="p-0 overflow-hidden d-flex flex-column" ref={containerRef} style={{ height: 'calc(100vh - 56px)', minHeight: '-webkit-fill-available', backgroundColor: '#f1f2f6' }}>
            <Row className="g-0 flex-grow-1 overflow-hidden">
                {/* 왼쪽 사이드바 (메뉴) */}
                {(!isMobile || mobileTab === 'menu') && (
                    <Col md={2} xs={12} className={`bg-white border-end p-3 overflow-auto shadow-sm d-flex flex-column ${isMobile ? 'position-absolute w-100 z-3' : 'h-100'}`} style={isMobile ? { top: 0, bottom: '60px' } : {}}>
                        <div className="mb-4 text-center border-bottom pb-3">
                            <h4 className="fw-bold mb-0 text-dark">{<FaDrawPolygon />} 와꾸</h4>
                        </div>

                        <Nav variant="pills" className="flex-column mb-4 gap-2 small fw-bold text-start">
                            <Nav.Link active={currentStep === 1} onClick={() => setCurrentStep(1)} className="rounded-3 p-2">1. 기초 외곽</Nav.Link>
                            <Nav.Link active={currentStep === 2} onClick={() => setCurrentStep(2)} className="rounded-3 p-2">2. 공간 구획</Nav.Link>
                            <Nav.Link active={currentStep === 3} onClick={() => setCurrentStep(3)} className="rounded-3 p-2">3. 가구 배치</Nav.Link>
                        </Nav>

                        <div className="flex-grow-1 overflow-auto pe-1">
                            <Button variant="outline-primary" className="w-100 mb-3 py-2 rounded-3 fw-bold small border-2" onClick={() => setShowGalleryModal(true)}>🎨 샘플 갤러리 열기</Button>

                            {currentStep === 1 && <Button variant="dark" className="w-100 py-3 rounded-4 shadow-sm fw-bold" onClick={() => setShowShellModal(true)}><FaVectorSquare className="me-2" /> 골조 설정</Button>}
                            {currentStep === 2 && ROOM_PRESETS.map(p => (
                                <Card key={p.type} className="mb-2 shadow-sm border-0 furniture-card" onClick={() => { addRoom(p); if (isMobile) setMobileTab('canvas'); }}>
                                    <Card.Body className="p-3 d-flex align-items-center gap-2"><div style={{ width: 20, height: 20, backgroundColor: p.color, borderRadius: 4 }}></div><span className="small fw-bold">{p.name}</span></Card.Body>
                                </Card>
                            ))}
                            {currentStep === 3 && FURNITURE_PRESETS.map(p => (
                                <Card key={p.type} className="mb-2 shadow-sm border-0 furniture-card" onClick={() => { addFurniture(p); if (isMobile) setMobileTab('canvas'); }}>
                                    <Card.Body className="p-3 d-flex align-items-center gap-2"><div style={{ width: 20, height: 20, backgroundColor: p.color, borderRadius: 4 }}></div><span className="small fw-bold">{p.name}</span></Card.Body>
                                </Card>
                            ))}
                        </div>
                    </Col>
                )}

                {/* 중앙 캔버스 영역 */}
                {(!isMobile || mobileTab === 'canvas') && (
                    <Col md={8} xs={12} className="d-flex flex-column position-relative h-100 p-0">
                        <div className="bg-white border-bottom p-2 d-flex justify-content-between px-3 shadow-sm z-2">
                            <div className="d-flex align-items-center gap-2 overflow-hidden">
                                <span className="fw-bold text-primary text-truncate small" style={{ maxWidth: '150px' }}>#{currentMapTitle || projectName}</span>
                            </div>
                            <div className="d-flex gap-2">
                                <ButtonGroup size="sm" className="shadow-sm">
                                    <Button variant="light" onClick={handleZoomIn}><FaSearchPlus /></Button>
                                    <Button variant="light" onClick={handleZoomOut}><FaSearchMinus /></Button>
                                    <Button variant="light" onClick={handleFitToScreen} title="화면 맞춤"><FaAlignCenter /></Button>
                                    <Button variant="light" onClick={() => handleRotatePlan(-1)} title="좌측 90도 회전"><FaUndoAlt /></Button>
                                    <Button variant="light" onClick={() => handleRotatePlan(1)} title="우측 90도 회전"><FaRedoAlt /></Button>
                                    <Button variant={is3DMode ? "primary" : "light"} onClick={() => setIs3DMode(!is3DMode)} title="3D 뷰 토글"><FaCube /></Button>
                                </ButtonGroup>
                                <Button variant="light" size="sm" onClick={toggleFullscreen}><FaExpand /></Button>
                                <div className="vr mx-1"></div>
                                <ButtonGroup size="sm"><Button variant="light" onClick={handleUndo} disabled={historyStep <= 0}><FaUndo /></Button><Button variant="light" onClick={handleRedo} disabled={historyStep >= history.length - 1}><FaRedo /></Button></ButtonGroup>
                                <Button variant="danger" size="sm" disabled={!selectedId} onClick={deleteSelected}><FaTrash /></Button>
                                {isMobile && isAuthenticated && (
                                    <ButtonGroup size="sm" className="ms-1">
                                        <Button variant="outline-success" onClick={() => setShowLoadModal(true)}><FaFolderOpen /></Button>
                                        <Button variant="success" onClick={() => { setTempSaveTitle(currentMapTitle || projectName); if (currentMapId) handleSave(); else setShowSaveModal(true); }}><FaSave /></Button>
                                    </ButtonGroup>
                                )}
                            </div>
                        </div>

                        <div className="flex-grow-1 bg-light position-relative overflow-hidden">
                            {is3DMode ? (
                                <WaGgu3DViewer items={items} shellConfig={shellConfig} />
                            ) : (
                                <Stage
                                    width={containerRef.current ? (isMobile ? containerRef.current.clientWidth : containerRef.current.clientWidth * 0.66) : window.innerWidth}
                                    height={containerRef.current ? (isMobile ? containerRef.current.clientHeight - 114 : containerRef.current.clientHeight - 54) : 800}
                                    scaleX={stageScale}
                                    scaleY={stageScale}
                                    x={stagePos.x}
                                    y={stagePos.y}
                                    ref={stageRef}
                                    draggable={!selectedId}
                                    onClick={handleSelect}
                                    onDragStart={(e) => {
                                        if (e.target === stageRef.current) setSelectedId(null);
                                    }}
                                    onWheel={handleWheel}
                                    onDragMove={(e) => {
                                        if (e.target === stageRef.current) setStagePos({ x: e.target.x(), y: e.target.y() });
                                        else handleDragMove(e);
                                    }}
                                    onDragEnd={handleDragEnd}
                                    style={{ backgroundColor: '#fff' }}
                                >
                                    <Layer>
                                        <Rect width={100000} height={100000} fill="#ffffff" x={-50000} y={-50000} listening={false}
                                            fillPatternImage={gridPattern} fillPatternScaleX={1 / stageScale} fillPatternScaleY={1 / stageScale}
                                        />
                                        {items.map((item) => (
                                            <Group key={item.id} id={item.id} x={item.x} y={item.y} width={item.width} height={item.height} rotation={item.rotation} draggable={item.draggable} onDragMove={handleDragMove} onDragEnd={handleDragEnd} onTransformEnd={handleTransformEnd}>
                                                {item.subType === 'door' ? <DoorSymbol width={item.width} height={item.height} color={item.color} /> :
                                                    item.subType === 'window' ? <WindowSymbol width={item.width} height={item.height} color={item.color} /> :
                                                        item.subType === 'sliding-door' ? <SlidingDoorSymbol width={item.width} height={item.height} color={item.color} /> :
                                                            <Rect width={item.width} height={item.height} fill={item.color} stroke={selectedId === item.id ? '#3498db' : '#2d3436'} strokeWidth={selectedId === item.id ? 2 / stageScale : 1 / stageScale} opacity={item.opacity || 1} cornerRadius={item.type === ITEM_TYPES.FURNITURE ? 4 : 0} />}
                                                {(item.type === ITEM_TYPES.FURNITURE || item.type === ITEM_TYPES.ROOM) && (
                                                    <Text
                                                        text={item.customName || item.name}
                                                        width={item.width}
                                                        height={item.subType === 'door' ? item.width : item.height}
                                                        align="center"
                                                        verticalAlign="middle"
                                                        fontSize={Math.max(12, 12 / stageScale)}
                                                        fill="#2d3436"
                                                        fontStyle="bold"
                                                    />
                                                )}
                                                {showDimensions && <>
                                                    {/* 가로 치수선 (상단) */}
                                                    <Line points={[0, -20 / stageScale, item.width, -20 / stageScale]} stroke="#2c3e50" strokeWidth={2 / stageScale} dash={[10 / stageScale, 5 / stageScale]} />
                                                    <Text x={item.width / 2 - 25 / stageScale} y={-40 / stageScale} text={`${Math.round(item.width * 10)}mm`} fontSize={12 / stageScale} fill="#1e272e" fontStyle="bold" />

                                                    {/* 세로 치수선 (우측) */}
                                                    <Line points={[item.width + 20 / stageScale, 0, item.width + 20 / stageScale, item.height]} stroke="#2c3e50" strokeWidth={2 / stageScale} dash={[10 / stageScale, 5 / stageScale]} />
                                                    <Text x={item.width + 35 / stageScale} y={item.height / 2 - 15 / stageScale} text={`${Math.round(item.height * 10)}mm`} fontSize={12 / stageScale} fill="#1e272e" fontStyle="bold" rotation={90} />
                                                </>}
                                            </Group>
                                        ))}

                                        {/* 스마트 가이드 렌더링 */}
                                        {guides.map((g, i) => (
                                            <Line
                                                key={i}
                                                points={g.orientation === 'V' ? [g.pos, -5000, g.pos, 5000] : [-5000, g.pos, 5000, g.pos]}
                                                stroke="#ff00ff"
                                                strokeWidth={1 / stageScale}
                                                dash={[4 / stageScale, 4 / stageScale]}
                                            />
                                        ))}

                                        {selectedId && (
                                            <Transformer
                                                ref={trRef}
                                                rotateEnabled={true}
                                                anchorSize={8 / stageScale}
                                                borderDash={[4, 4]}
                                                enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top-center', 'bottom-center', 'left-center', 'right-center']}
                                                rotationSnaps={[0, 90, 180, 270]}
                                                rotationSnapTolerance={10}
                                            />
                                        )}
                                    </Layer>
                                </Stage>
                            )}
                            <div className="position-absolute bottom-0 end-0 m-3 px-3 py-1 bg-white shadow-sm rounded-pill small border">Zoom: {Math.round(stageScale * 100)}%</div>
                        </div>
                    </Col>
                )}

                {/* 오른쪽 사이드바 (설정) */}
                {(!isMobile || mobileTab === 'settings') && (
                    <Col md={2} xs={12} className={`bg-white border-start p-3 overflow-auto shadow-sm ${isMobile ? 'position-absolute w-100 z-3' : 'h-100'}`} style={isMobile ? { top: 0, bottom: '60px' } : {}}>
                        <h6 className="fw-bold mb-3 border-bottom pb-2">설정 및 속성</h6>
                        <div className="mb-4">
                            <Form.Check type="switch" label="치수 및 보조선 표시" checked={showDimensions} onChange={e => setShowDimensions(e.target.checked)} className="small fw-bold" />
                        </div>
                        {selectedItem ? (
                            <div className="p-3 bg-light rounded-3 border">
                                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2"><div style={{ width: 12, height: 12, backgroundColor: selectedItem.color, borderRadius: 2 }}></div>{selectedItem.name} 속성</h6>
                                <Form.Group className="mb-3">
                                    <Form.Label className="tiny fw-bold text-muted">가로 (mm)</Form.Label>
                                    <Form.Control size="sm" type="number" value={Math.round(selectedItem.width * 10)} onChange={e => updateItemProperty(selectedId, 'width', parseFloat(e.target.value) / 10)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="tiny fw-bold text-muted">세로 (mm)</Form.Label>
                                    <Form.Control size="sm" type="number" value={Math.round(selectedItem.height * 10)} onChange={e => updateItemProperty(selectedId, 'height', parseFloat(e.target.value) / 10)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="tiny fw-bold text-muted">이름 변경</Form.Label>
                                    <Form.Control size="sm" type="text" value={selectedItem.customName || ''} onChange={e => updateItemProperty(selectedId, 'customName', e.target.value)} placeholder={selectedItem.name} />
                                </Form.Group>
                                <hr />
                                <div className="d-flex justify-content-between mt-1 fw-bold text-primary"><span>면적</span><span>{calculateArea(selectedItem.width, selectedItem.height).m2}㎡ ({calculateArea(selectedItem.width, selectedItem.height).py}평)</span></div>
                                <Button variant="outline-danger" size="sm" className="w-100 mt-3 fw-bold" onClick={deleteSelected}><FaTrash className="me-2" />삭제하기</Button>
                            </div>
                        ) : (
                            <div className="text-center py-5 text-muted small">편집할 요소를 선택하세요</div>
                        )}
                        <hr />
                        {isAuthenticated ? (
                            <div className="d-flex flex-column gap-2 mb-3">
                                <Button variant="outline-success" className="w-100 fw-bold py-2 rounded-3 border-2 shadow-sm" onClick={() => setShowLoadModal(true)}>
                                    <FaFolderOpen className="me-2" />불러오기
                                </Button>
                                <Button variant="success" className="w-100 fw-bold py-2 rounded-3 shadow-sm" onClick={() => {
                                    setTempSaveTitle(currentMapTitle || projectName);
                                    if (currentMapId) handleSave();
                                    else setShowSaveModal(true);
                                }}>
                                    <FaSave className="me-2" />저장하기
                                </Button>
                                {currentMapId && (
                                    <Button variant="outline-primary" size="sm" className="w-100 fw-bold py-1 rounded-3" onClick={() => {
                                        setTempSaveTitle(currentMapTitle || projectName);
                                        setCurrentMapId(null);
                                        setShowSaveModal(true);
                                    }}>
                                        다른 이름으로 저장
                                    </Button>
                                )}
                                <Button variant="outline-dark" className="w-100 fw-bold py-2 rounded-3 shadow-sm mt-2 d-none" onClick={handleExtract}>
                                    <FaDownload className="me-2" />추출하기
                                </Button>

                                <div className="d-flex gap-2 w-100 mt-2">
                                    <Button variant="danger" size="sm" className="w-50 fw-bold py-2 rounded-3" onClick={handleExportPDF}>
                                        <FaFilePdf className="me-1" /> PDF 저장
                                    </Button>
                                    <Button variant="info" size="sm" className="w-50 text-white fw-bold py-2 rounded-3" onClick={handleExportPNG}>
                                        <FaFileImage className="me-1" /> PNG 저장
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center small text-muted mb-3">로그인 후 저장 기능을<br />사용할 수 있습니다.</div>
                        )}
                    </Col>
                )}
            </Row>

            {/* 모바일 하단 탭 네비게이션 */}
            {isMobile && (
                <div className="bg-white border-top shadow-lg p-2 d-flex justify-content-around z-3 w-100" style={{ height: '60px' }}>
                    <Button variant={mobileTab === 'menu' ? 'primary' : 'light'} onClick={() => setMobileTab('menu')} className="flex-fill mx-1 rounded-3 border-0">
                        <FaLayerGroup className="mb-1" /><br /><span className="tiny fw-bold">메뉴</span>
                    </Button>
                    <Button variant={mobileTab === 'canvas' ? 'primary' : 'light'} onClick={() => setMobileTab('canvas')} className="flex-fill mx-1 rounded-3 border-0">
                        <FaVectorSquare className="mb-1" /><br /><span className="tiny fw-bold">캔버스</span>
                    </Button>
                    <Button variant={mobileTab === 'settings' ? 'primary' : 'light'} onClick={() => setMobileTab('settings')} className="flex-fill mx-1 rounded-3 border-0">
                        <FaCog className="mb-1" /><br /><span className="tiny fw-bold">설정</span>
                    </Button>
                </div>
            )}

            <Modal show={showShellModal} onHide={() => { setShowShellModal(false); setSelectedPresetArea(null); }} centered size="lg">
                <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold">골조 설정 (평형 선택)</Modal.Title></Modal.Header>
                <Modal.Body className="p-4">
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="small fw-bold">가공 면적 프리셋 ({areaUnit === 'm2' ? '㎡' : '평'})</span>
                            <ButtonGroup size="sm"><Button variant={areaUnit === 'm2' ? 'primary' : 'outline-primary'} onClick={() => setAreaUnit('m2')}>㎡</Button><Button variant={areaUnit === 'py' ? 'primary' : 'outline-primary'} onClick={() => setAreaUnit('py')}>평</Button></ButtonGroup>
                        </div>
                        <Row className="g-2 mb-3">
                            {AREA_PRESETS.map(area => (
                                <Col xs={3} key={area}>
                                    <Button variant={selectedPresetArea === area ? 'primary' : 'outline-dark'} className="w-100 py-2 small fw-bold" onClick={() => handleAreaPreset(area)}>
                                        {areaUnit === 'm2' ? `${area}㎡` : `${(area / 3.3058).toFixed(0)}평`}
                                    </Button>
                                </Col>
                            ))}
                        </Row>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="small fw-bold">가로:세로 비율 선택</span>
                            <ButtonGroup size="sm">
                                <Button variant={shellRatio === '4:3' ? 'dark' : 'outline-dark'} onClick={() => handleRatioChange('4:3')}>4:3 (직사각형)</Button>
                                <Button variant={shellRatio === '1:1' ? 'dark' : 'outline-dark'} onClick={() => handleRatioChange('1:1')}>1:1 (정사각형)</Button>
                            </ButtonGroup>
                        </div>
                    </div>

                    <Row className="mb-4">
                        <Col><Form.Group><Form.Label className="small fw-bold">가로 길이 (mm)</Form.Label><Form.Control type="number" value={shellConfig.width * 10} onChange={e => { setShellConfig({ ...shellConfig, width: Number(e.target.value) / 10 }); setSelectedPresetArea(null); }} className="bg-light border-0 py-2" /></Form.Group></Col>
                        <Col><Form.Group><Form.Label className="small fw-bold">세로 길이 (mm)</Form.Label><Form.Control type="number" value={shellConfig.height * 10} onChange={e => { setShellConfig({ ...shellConfig, height: Number(e.target.value) / 10 }); setSelectedPresetArea(null); }} className="bg-light border-0 py-2" /></Form.Group></Col>
                    </Row>

                    <div className="p-3 bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-4 text-center">
                        <span className="text-primary fw-bold fs-5">계산된 면적: {calculateArea(shellConfig.width, shellConfig.height).m2}㎡ ({calculateArea(shellConfig.width, shellConfig.height).py}평)</span>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 p-3"><Button variant="light" onClick={() => { setShowShellModal(false); setSelectedPresetArea(null); }} className="rounded-pill px-4">취소</Button><Button variant="primary" onClick={createExteriorShell} className="rounded-pill px-4 fw-bold shadow-sm px-5">골조 생성하기</Button></Modal.Footer>
            </Modal>

            <Modal show={showGalleryModal} onHide={() => setShowGalleryModal(false)} centered size="xl" onExited={() => applySample(pendingSample)}>
                <Modal.Header closeButton className="border-0"><Modal.Title className="fw-bold fs-4">📋 와꾸 샘플 갤러리</Modal.Title></Modal.Header>
                <Modal.Body className="p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <p className="text-muted mb-4">다양한 라이프스타일이 반영된 20개의 도면 템플릿입니다. 원하는 도면을 선택해 바로 수정을 시작하세요.</p>
                    <Row className="g-3">
                        {SAMPLES.map(sample => (
                            <Col md={4} lg={3} key={sample.id}>
                                <Card className="h-100 border-0 shadow-sm sample-card" onClick={() => loadSample(sample)}>
                                    <div className="bg-light d-flex align-items-center justify-content-center border-bottom" style={{ height: 120 }}>
                                        <div className="text-primary fw-bold" style={{ opacity: 0.3 }}>{sample.type} PLAN</div>
                                    </div>
                                    <Card.Body className="p-3">
                                        <h6 className="fw-bold mb-1">{sample.title}</h6>
                                        <p className="tiny text-muted mb-0">{sample.desc}</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0"><Button variant="light" onClick={() => setShowGalleryModal(false)} className="rounded-pill px-4">닫기</Button></Modal.Footer>
            </Modal>

            {/* --- CRUD 모달 영역 --- */}

            {/* 1. 저장하기 제목 입력 모달 */}
            <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)} centered className="forge-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold"><FaSave className="me-2 text-success" /> 도면 저장</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pb-4">
                    <Form onSubmit={handleSave}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted">도면 이름 (제목)</Form.Label>
                            <Form.Control
                                autoFocus
                                type="text"
                                placeholder="예: 우리집 거실 배치도, 꿈의 34평형..."
                                value={tempSaveTitle}
                                onChange={(e) => setTempSaveTitle(e.target.value)}
                                className="rounded-3 border-0 bg-light p-3 shadow-none"
                            />
                        </Form.Group>
                        <div className="d-grid mt-4">
                            <Button variant="success" type="submit" disabled={isSaving} className="rounded-pill py-2 fw-bold">
                                {isSaving ? '저장 중...' : '저장소에 보관하기'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* 2. 불러오기 목록 모달 (도면 저장소) */}
            <Modal show={showLoadModal} onHide={() => setShowLoadModal(false)} centered size="lg" className="forge-modal">
                <Modal.Header closeButton className="border-bottom px-4">
                    <Modal.Title className="fw-bold"><FaFolderOpen className="me-2 text-primary" /> 도면 저장소</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {isLoadingList ? (
                        <div className="text-center py-5">
                            <FaCogs className="fa-spin text-muted opacity-25" size={40} />
                            <p className="mt-3 text-muted">저장소를 뒤적거리는 중...</p>
                        </div>
                    ) : savedItems.length > 0 ? (
                        <>
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr className="small text-muted">
                                        <th className="px-4 py-3">제목</th>
                                        <th className="py-3" style={{ width: '150px' }}>저장일시</th>
                                        <th className="px-4 py-3 text-end" style={{ width: '120px' }}>작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {savedItems.map((item) => (
                                        <tr key={item.id} className={`align-middle cursor-pointer ${currentMapId === item.id ? 'table-active' : ''}`} onClick={() => handleLoadItem(item)}>
                                            <td className="px-4 fw-bold">{item.title} {currentMapId === item.id && <span className="badge bg-primary ms-2">현재</span>}</td>
                                            <td className="text-muted small">
                                                {item.created_at ? new Date(item.created_at).toLocaleString('ko-KR', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) : '-'}
                                            </td>
                                            <td className="px-4 text-end" onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    variant="link"
                                                    className="text-danger p-1"
                                                    onClick={() => handleDeleteServerItem(item.id)}
                                                    title="파기"
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {/* 페이징 처리 */}
                            {totalCount > itemsPerPage && (
                                <div className="d-flex justify-content-center py-3 border-top gap-2 bg-light">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="rounded-circle"
                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    >
                                        &lt;
                                    </Button>
                                    <span className="align-self-center small fw-bold mx-2">
                                        Page {currentPage} of {Math.ceil(totalCount / itemsPerPage)}
                                    </span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="rounded-circle"
                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                        disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                    >
                                        &gt;
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-5">
                            <FaFolderOpen className="text-muted opacity-10" size={60} />
                            <p className="mt-3 text-muted">저장된 도면이 없습니다.<br />나만의 도면을 그려서 저장해 보세요!</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setShowLoadModal(false)} className="rounded-pill px-4 fw-bold">닫기</Button>
                </Modal.Footer>
            </Modal>

            <style>{`.forge-modal .modal-content { border: none; border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.1); } .cursor-pointer { cursor: pointer; } .furniture-card:hover { transform: translateY(-2px); cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important; } .sample-card { cursor: pointer; transition: all 0.2s ease-in-out; border-radius: 12px; overflow: hidden; } .sample-card:hover { transform: scale(1.03); box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important; } .tiny { font-size: 0.75rem; } .z-3 { z-index: 1050; }`}</style>
        </Container>
    );
};

export default WaGgu;
